import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import {
  Layers, Users, TrendingUp, UserCheck, AlertTriangle, Download, Search, ArrowUpDown,
} from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';
import { trainerApi, type TrainerBatch, type BatchOverview } from '@/lib/api/trainer';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { formatTimestamp } from '@/lib/date';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/trainer/analytics')({
  beforeLoad: () => {
    const { isAuthenticated, hasRole } = useAuthStore.getState();
    if (!isAuthenticated) throw redirect({ to: '/login' });
    if (!hasRole('trainer', 'super_admin')) throw redirect({ to: '/dashboard' });
  },
  component: TrainerAnalyticsPage,
});

// ── helpers ──────────────────────────────────────────────────────────────────

function batchStatus(b: TrainerBatch): 'active' | 'upcoming' | 'ended' {
  const today = new Date().toISOString().slice(0, 10);
  if (!b.start_date) return 'upcoming';
  if (b.start_date > today) return 'upcoming';
  if (b.end_date && b.end_date < today) return 'ended';
  return 'active';
}

const C = {
  blue:  '#ff5a1f',
  green: '#22c55e',
  amber: '#f59e0b',
  red:   '#ef4444',
  muted: '#94a3b8',
  teal:  '#0f766e',
};

const RISK_THRESHOLD = 20;

type StatusFilter = 'at_risk' | 'on_track' | 'advanced' | 'all';
type SortKey = 'progress' | 'attendance';

function initials(name: string): string {
  return name.split(/[\s._-]+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// ── local shared components ───────────────────────────────────────────────────

function ChartCard({ title, subtitle, right, children }: {
  title: string; subtitle?: string; right?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-[#ececec] bg-white">
      <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
        <div>
          <h3 className="text-[13px] font-semibold text-[#111827]">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11px] text-[#6b7280]">{subtitle}</p>}
        </div>
        {right}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon, color, iconBg, alert, onClick }: {
  label: string; value: number | string; sub?: React.ReactNode;
  icon: React.ReactNode; color: string; iconBg: string; alert?: boolean;
  onClick?: () => void;
}) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      onClick={onClick}
      className={cn(
        'rounded-lg border bg-white p-4 text-left transition-colors',
        alert ? 'border-red-200 bg-gradient-to-b from-white to-red-50/60' : 'border-[#ececec]',
        onClick && 'hover:border-foreground/20 cursor-pointer',
      )}
    >
      <div className="mb-2.5 flex items-center gap-2.5">
        <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
          {icon}
        </div>
        <span className="text-[10.5px] font-bold uppercase tracking-widest text-[#6b7280]/80">
          {label}
        </span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-3xl font-bold leading-none ${color}`}>{value}</span>
      </div>
      {sub && <div className="mt-2 text-[11.5px] text-[#6b7280]">{sub}</div>}
    </Comp>
  );
}

function RechartsTooltip({ active, payload, label }: {
  active?: boolean; payload?: any[]; label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[#ececec] bg-white px-3 py-2 shadow-md text-[12px]">
      {label && <p className="mb-1 font-semibold text-[#111827]">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color ?? p.fill }}>
          {p.name}: <span className="font-semibold">{p.value}%</span>
        </p>
      ))}
    </div>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

function TrainerAnalyticsPage() {
  const [batches, setBatches] = useState<TrainerBatch[]>([]);
  const [overviews, setOverviews] = useState<BatchOverview[]>([]);
  const [loading, setLoading] = useState(true);

  // table controls
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [batchFilter, setBatchFilter] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('progress');
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    trainerApi.listBatches()
      .then(res => {
        const bs = res.data ?? [];
        setBatches(bs);
        return Promise.all(bs.map(b => trainerApi.getBatchOverview(b.id)));
      })
      .then(responses => setOverviews(responses.map(r => r.data)))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  const totalStudents = useMemo(
    () => batches.reduce((s, b) => s + b.student_count, 0),
    [batches],
  );

  const avgProgress = useMemo(() => {
    if (!batches.length) return 0;
    const sum = batches.reduce((s, b) => s + (b.progress_pct ?? 0), 0);
    return Math.round(sum / batches.length);
  }, [batches]);

  const avgAttendance = useMemo(() => {
    if (!overviews.length) return 0;
    const sum = overviews.reduce((s, o) => s + (o.stats.attendance_avg ?? 0), 0);
    return Math.round(sum / overviews.length);
  }, [overviews]);

  const statusCounts = useMemo(() => {
    const counts = { active: 0, upcoming: 0, ended: 0 };
    batches.forEach(b => { counts[batchStatus(b)]++; });
    return counts;
  }, [batches]);

  // all students flattened with batch context
  const allStudents = useMemo(() =>
    overviews.flatMap((o, i) =>
      o.students.map(s => ({
        ...s,
        batchName: batches[i]?.name ?? '—',
        batchId:   batches[i]?.id ?? '',
      }))
    ),
    [overviews, batches],
  );

  const atRiskCount = useMemo(
    () => allStudents.filter(s => (s.progress_pct ?? 0) < RISK_THRESHOLD).length,
    [allStudents],
  );

  // default the table to the actionable view when there's something to act on
  useEffect(() => {
    if (!loading && atRiskCount > 0) setStatusFilter('at_risk');
  }, [loading, atRiskCount]);

  // ── chart data ────────────────────────────────────────────────────────────
  const batchPerformanceData = useMemo(() =>
    batches.map((b, i) => ({
      name: b.name,
      short: b.name.length > 22 ? b.name.slice(0, 20) + '…' : b.name,
      Progress: Math.round(b.progress_pct ?? 0),
      Attendance: Math.round(overviews[i]?.stats.attendance_avg ?? 0),
    })),
    [batches, overviews],
  );

  const studentDistData = useMemo(() => {
    const counts = { 'Not Started': 0, 'In Progress': 0, 'Advanced': 0, 'Completed': 0 };
    allStudents.forEach(s => {
      const p = s.progress_pct ?? 0;
      if (p === 0)       counts['Not Started']++;
      else if (p < 75)   counts['In Progress']++;
      else if (p < 100)  counts['Advanced']++;
      else               counts['Completed']++;
    });
    return [
      { name: 'Not Started', value: counts['Not Started'], color: C.muted },
      { name: 'In Progress', value: counts['In Progress'], color: C.blue },
      { name: 'Advanced',    value: counts['Advanced'],    color: C.amber },
      { name: 'Completed',   value: counts['Completed'],   color: C.green },
    ].filter(d => d.value > 0);
  }, [allStudents]);

  // students scoped by batch + search (but NOT status) — drives both the
  // status-tab counts and the visible rows, so the counts track the filters.
  const scopedStudents = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allStudents.filter(s => {
      if (batchFilter !== 'all' && s.batchId !== batchFilter) return false;
      if (q && !s.username.toLowerCase().includes(q) && !s.batchName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allStudents, batchFilter, query]);

  // ── filtered + sorted table rows ────────────────────────────────────────────
  const rows = useMemo(() => {
    const filtered = scopedStudents.filter(s => {
      const p = s.progress_pct ?? 0;
      if (statusFilter === 'at_risk'  && !(p < RISK_THRESHOLD)) return false;
      if (statusFilter === 'on_track' && !(p >= RISK_THRESHOLD && p < 75)) return false;
      if (statusFilter === 'advanced' && !(p >= 75)) return false;
      return true;
    });
    const dir = sortAsc ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sortKey === 'progress' ? (a.progress_pct ?? 0) : (a.attendance_pct ?? 0);
      const bv = sortKey === 'progress' ? (b.progress_pct ?? 0) : (b.attendance_pct ?? 0);
      return (av - bv) * dir;
    });
  }, [scopedStudents, statusFilter, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(v => !v);
    else { setSortKey(key); setSortAsc(true); }
  }

  function studentStatus(pct: number): { label: string; cls: string } {
    if (pct < RISK_THRESHOLD) return { label: 'At Risk',  cls: 'border-red-300 bg-red-50 text-red-600' };
    if (pct < 75)             return { label: 'On Track', cls: 'border-[#ffd9c7] bg-[#fff1ec] text-[#ff5a1f]' };
    return                           { label: 'Advanced', cls: 'border-green-300 bg-green-50 text-green-600' };
  }

  function exportCsv() {
    const header = ['Student', 'Batch', 'Progress %', 'Attendance %', 'Last Active'];
    const lines = rows.map(s => [
      s.username,
      s.batchName,
      Math.round(s.progress_pct ?? 0),
      Math.round(s.attendance_pct ?? 0),
      s.last_active ? formatTimestamp(s.last_active) : '',
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'trainer-analytics.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} ${rows.length === 1 ? 'student' : 'students'}`);
  }

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-9 w-40 rounded-lg" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-[104px] rounded-lg" />)}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
          <Skeleton className="h-[340px] rounded-lg" />
          <Skeleton className="h-[340px] rounded-lg" />
        </div>
        <Skeleton className="h-[360px] rounded-lg" />
      </div>
    );
  }

  if (!batches.length) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-3 text-center">
        <Layers className="h-10 w-10 text-[#9ca3af]" />
        <h1 className="text-[18px] font-semibold text-[#111827]">No batches yet</h1>
        <p className="max-w-sm text-[13px] text-[#6b7280]">
          Analytics will appear once you have batches assigned to you.
        </p>
      </div>
    );
  }

  const filterTabs: { key: StatusFilter; label: string; count: number; risk?: boolean }[] = [
    { key: 'at_risk',  label: 'At risk',   count: scopedStudents.filter(s => (s.progress_pct ?? 0) < RISK_THRESHOLD).length, risk: true },
    { key: 'on_track', label: 'On track',  count: scopedStudents.filter(s => (s.progress_pct ?? 0) >= RISK_THRESHOLD && (s.progress_pct ?? 0) < 75).length },
    { key: 'advanced', label: 'Advanced',  count: scopedStudents.filter(s => (s.progress_pct ?? 0) >= 75).length },
    { key: 'all',      label: 'All',       count: scopedStudents.length },
  ];

  return (
    <div className="space-y-4">

      {/* header + controls */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#111827]">Analytics</h1>
          <p className="mt-1 text-[13px] text-[#6b7280]">
            {batches.length} {batches.length === 1 ? 'batch' : 'batches'} · {totalStudents} students
          </p>
        </div>
        <button
          onClick={exportCsv}
          className="inline-flex items-center gap-2 rounded-lg border border-[#ececec] bg-white px-3 py-2 text-[13px] font-semibold text-[#6b7280] transition-colors hover:border-foreground/20 hover:text-[#111827]"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <KpiCard
          label="Batches" value={batches.length}
          icon={<Layers className="h-4 w-4 text-[#ff5a1f]" />} color="text-[#ff5a1f]" iconBg="bg-[#fff1ec]"
          sub={<span>{statusCounts.active} active · {statusCounts.ended} ended</span>}
        />
        <KpiCard
          label="Students" value={totalStudents}
          icon={<Users className="h-4 w-4 text-teal-600" />} color="text-teal-600" iconBg="bg-teal-50"
        />
        <KpiCard
          label="Avg Progress" value={`${avgProgress}%`}
          icon={<TrendingUp className="h-4 w-4 text-green-600" />} color="text-green-600" iconBg="bg-green-50"
        />
        <KpiCard
          label="Avg Attendance" value={`${avgAttendance}%`}
          icon={<UserCheck className="h-4 w-4 text-amber-600" />} color="text-amber-600" iconBg="bg-amber-50"
        />
        <KpiCard
          label="At Risk" value={atRiskCount} alert
          icon={<AlertTriangle className="h-4 w-4 text-red-500" />} color="text-red-500" iconBg="bg-red-50"
          sub={<span>below {RISK_THRESHOLD}% progress</span>}
          onClick={() => setStatusFilter('at_risk')}
        />
      </div>

      {/* charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">

        <ChartCard
          title="Batch performance"
          subtitle="Progress vs. attendance per batch"
          right={
            <div className="flex items-center gap-4 text-[11.5px] text-[#6b7280]">
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.blue }} />Progress</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.amber }} />Attendance</span>
            </div>
          }
        >
          {batchPerformanceData.length === 0 ? (
            <div className="flex h-[240px] items-center justify-center text-[13px] text-[#6b7280]">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, batchPerformanceData.length * 46)}>
              <BarChart data={batchPerformanceData} layout="vertical" barGap={2} margin={{ top: 4, right: 28, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F4" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10.5, fill: '#94a3b8' }} axisLine={false} tickLine={false} unit="%" />
                <YAxis type="category" dataKey="short" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={150} interval={0} />
                <Tooltip content={<RechartsTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
                <Bar dataKey="Progress"   fill={C.blue}  radius={[0, 4, 4, 0]} maxBarSize={12} isAnimationActive={false} />
                <Bar dataKey="Attendance" fill={C.amber} radius={[0, 4, 4, 0]} maxBarSize={12} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Student progress" subtitle={`Distribution across ${totalStudents} students`}>
          {studentDistData.length === 0 ? (
            <div className="flex h-[196px] items-center justify-center text-[13px] text-[#6b7280]">No students yet</div>
          ) : (
            <>
              <div className="flex items-center gap-5">
                <div className="relative h-[150px] w-[150px] flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={studentDistData} cx="50%" cy="50%" innerRadius={50} outerRadius={72} paddingAngle={3} dataKey="value" strokeWidth={0} isAnimationActive={false}>
                        {studentDistData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold leading-none tabular-nums text-[#111827]">{totalStudents}</span>
                    <span className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-[#6b7280]">Students</span>
                  </div>
                </div>
                <div className="flex flex-1 flex-col gap-2.5">
                  {studentDistData.map(d => (
                    <div key={d.name} className="flex items-center gap-2 text-[12.5px]">
                      <span className="h-2.5 w-2.5 flex-shrink-0 rounded-sm" style={{ backgroundColor: d.color }} />
                      <span className="text-[#6b7280]">{d.name}</span>
                      <span className="ml-auto font-semibold tabular-nums text-[#111827]">{d.value}</span>
                      <span className="w-9 text-right text-[11px] tabular-nums text-[#6b7280]">
                        {Math.round((d.value / Math.max(1, totalStudents)) * 100)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* batch status chips */}
              <div className="mt-5 grid grid-cols-3 gap-2.5">
                {[
                  { n: statusCounts.active,   l: 'Active',   color: C.green, cls: 'text-green-600' },
                  { n: statusCounts.ended,    l: 'Ended',    color: C.muted, cls: 'text-[#6b7280]' },
                  { n: statusCounts.upcoming, l: 'Upcoming', color: C.blue,  cls: 'text-[#ff5a1f]' },
                ].map(s => (
                  <div key={s.l} className="rounded-lg border border-[#ececec] px-3 py-2.5">
                    <div className={`text-2xl font-bold leading-none ${s.cls}`}>{s.n}</div>
                    <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-[#6b7280]">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />{s.l}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </ChartCard>

      </div>

      {/* students table */}
      <div className="rounded-lg border border-[#ececec] bg-white">
        <div className="flex flex-wrap items-center gap-3 border-b border-[#ececec] px-5 py-4">
          <h3 className="text-[14px] font-semibold text-[#111827]">Students</h3>

          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#6b7280]" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search students…"
              className="h-9 w-[200px] pl-8 text-[13px]"
            />
          </div>

          <Select value={batchFilter} onValueChange={setBatchFilter}>
            <SelectTrigger className="h-9 w-[190px] text-[13px]">
              <SelectValue placeholder="All batches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All batches</SelectItem>
              {batches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="ml-auto inline-flex rounded-lg bg-[#f3f4f6] p-1">
            {filterTabs.map(t => (
              <button
                key={t.key}
                onClick={() => setStatusFilter(t.key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors',
                  statusFilter === t.key
                    ? cn('bg-white shadow-sm', t.risk ? 'text-red-600' : 'text-[#111827]')
                    : 'text-[#6b7280] hover:text-[#111827]',
                )}
              >
                {t.label} · {t.count}
              </button>
            ))}
          </div>
        </div>

        <div className="px-2 pb-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10.5px] font-bold uppercase tracking-wider text-[#6b7280]">Student</TableHead>
                <TableHead className="text-[10.5px] font-bold uppercase tracking-wider text-[#6b7280]">Batch</TableHead>
                <TableHead className="text-right">
                  <button onClick={() => toggleSort('progress')} className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider text-[#6b7280] hover:text-[#111827]">
                    Progress <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="text-right">
                  <button onClick={() => toggleSort('attendance')} className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider text-[#6b7280] hover:text-[#111827]">
                    Attendance <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="text-right text-[10.5px] font-bold uppercase tracking-wider text-[#6b7280]">Last Active</TableHead>
                <TableHead className="text-[10.5px] font-bold uppercase tracking-wider text-[#6b7280]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-[13px] text-[#6b7280]">
                    No students match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((s, i) => {
                  const status = studentStatus(s.progress_pct ?? 0);
                  const pct = Math.round(s.progress_pct ?? 0);
                  const barColor = pct < RISK_THRESHOLD ? 'bg-red-500' : pct < 75 ? 'bg-[#ff5a1f]' : 'bg-green-500';
                  return (
                    <TableRow key={`${s.batchId}-${s.id}-${i}`}>
                      <TableCell className="py-2.5">
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#fff1ec] text-[11px] font-bold text-[#ff5a1f]">
                            {initials(s.username)}
                          </span>
                          <span className="text-[13px] font-medium text-[#111827]">{s.username}</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 text-[13px] text-[#6b7280]">
                        <Link
                          to="/trainer/batches/$batchId"
                          params={{ batchId: s.batchId }}
                          className="hover:text-[#ff5a1f] hover:underline"
                        >
                          {s.batchName}
                        </Link>
                      </TableCell>
                      <TableCell className="py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-[#f3f4f6]">
                            <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                          <span className="w-9 text-right text-[12px] tabular-nums text-[#6b7280]">{pct}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 text-right text-[12px] tabular-nums text-[#6b7280]">
                        {Math.round(s.attendance_pct ?? 0)}%
                      </TableCell>
                      <TableCell className="py-2.5 text-right text-[12px] tabular-nums text-[#6b7280]">
                        {s.last_active ? formatTimestamp(s.last_active) : '—'}
                      </TableCell>
                      <TableCell className="py-2.5">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${status.cls}`}>
                          {status.label}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

    </div>
  );
}
